import { apiClient } from '../utils/api';

// Types for Availability Schedule
export interface DaySchedule {
  day: string;
  isAvailable: boolean;
  startTime: string;
  endTime: string;
}

export interface VendorAvailability {
  vendorId: string;
  weeklySchedule: DaySchedule[];
}

export interface AvailabilityApiResponse {
  success: boolean;
  data: VendorAvailability;
  message: string;
}

// Types for Unavailable Dates
export interface UnavailableDate {
  availabilityId: string;
  vendorId: string;
  startDate: string;
  endDate: string;
  reason?: string;
  repeatType?: 'none' | 'monthly' | 'yearly';
}

export interface UnavailableDatesResponse {
  success: boolean;
  data: {
    vendorId: string;
    unavailableDates: UnavailableDate[];
  };
  message: string;
}

export interface AddUnavailableDatePayload {
  vendorId: string;
  startDate: string;
  endDate: string;
  reason?: string;
  repeatType?: 'none' | 'monthly' | 'yearly';
}



// Get vendor availability schedule
export const getVendorAvailability = async (vendorId: string): Promise<AvailabilityApiResponse> => {
  const response = await apiClient.get<AvailabilityApiResponse>(
    `/vendors/${vendorId}/availabilities`
  );
  return response.data;
};

// Update vendor availability schedule
export const updateVendorAvailability = async (
  vendorId: string,
  weeklySchedule: DaySchedule[]
): Promise<AvailabilityApiResponse> => {
  // Transform frontend format to backend format
  const days: Record<string, { available: boolean; from?: string; to?: string }> = {};
  
  weeklySchedule.forEach(schedule => {
    // Map day names: Monday -> Mon, Tuesday -> Tue, etc.
    const dayMap: Record<string, string> = {
      'Monday': 'Mon',
      'Tuesday': 'Tue',
      'Wednesday': 'Wed',
      'Thursday': 'Thu',
      'Friday': 'Fri',
      'Saturday': 'Sat',
      'Sunday': 'Sun'
    };
    
    const shortDay = dayMap[schedule.day] || schedule.day;
    days[shortDay] = {
      available: schedule.isAvailable,
      from: schedule.isAvailable ? schedule.startTime : undefined,
      to: schedule.isAvailable ? schedule.endTime : undefined
    };
  });

  const requestBody = {
    vendorId,
    name: '', // You may want to get this from vendor context
    availability: {
      sameForAllDays: false,
      days
    },
    status: 'active' as const
  };

  const response = await apiClient.put<AvailabilityApiResponse>(
    `/vendors/${vendorId}/availabilities`,
    requestBody
  );
  return response.data;
};

// Get unavailable dates
export const getUnavailableDates = async (vendorId: string): Promise<UnavailableDatesResponse> => {
  const response = await apiClient.get<UnavailableDatesResponse>(
    `/vendors/${vendorId}/availabilities/unavailable-dates`
  );
  return response.data;
};

// Add unavailable date
export const addUnavailableDate = async (
  payload: AddUnavailableDatePayload
): Promise<{ success: boolean; data: UnavailableDate; message: string }> => {
  const { vendorId, ...body } = payload;
  const response = await apiClient.post(
    `/vendors/${vendorId}/availabilities/unavailable-dates`,
    body
  );
  return response.data;
};

// Remove unavailable date
export const removeUnavailableDate = async (
  vendorId: string,
  availabilityId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.delete(
    `/vendors/${vendorId}/availabilities/unavailable-dates/${availabilityId}`
  );
  return response.data;
};
